// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { ChargeItemDefinition } from '@medplum/fhirtypes';
import { Loading, useMedplum } from '@medplum/react';
import * as Sentry from '@sentry/react';
import { IconLock } from '@tabler/icons-react';
import type { JSX } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import classes from './ChargemasterTab.module.css';

type Price = number | undefined;

interface ChargeRow {
  definition: ChargeItemDefinition;
  title: string;
  codes: string[];
  insurance: Price;
  discount: Price;
  selfPay: Price;
  insuranceReason?: string;
  selfPayReason?: string;
}

const SELF_PAY_REASONS: Record<string, string> = {
  '99453': 'Included in price of appts with clinician',
};

function getPrice(definition: ChargeItemDefinition, payer: 'insurance' | 'self-pay'): Price {
  const group = definition.propertyGroup?.find((candidate) =>
    candidate.applicability?.some((applicability) => applicability.id === payer || applicability.description === payer)
  );
  const amount = group?.priceComponent?.find((component) => component.type === 'base')?.amount?.value;
  return typeof amount === 'number' ? amount : undefined;
}

function getCode(definition: ChargeItemDefinition): string | undefined {
  return definition.code?.coding?.find((coding) => coding.system === 'http://www.ama-assn.org/go/cpt')?.code;
}

function displayTitle(definition: ChargeItemDefinition): string {
  if (definition.url?.endsWith('/self-pay-bundle')) {
    return '“Get Answers” bundle';
  }
  return definition.title ?? definition.code?.text ?? 'Untitled service';
}

export function getRows(definitions: ChargeItemDefinition[]): ChargeRow[] {
  const rows = new Map<string, ChargeRow>();
  for (const definition of definitions) {
    const code = getCode(definition);
    const insurance = getPrice(definition, 'insurance');
    const selfPay = getPrice(definition, 'self-pay');
    const title = displayTitle(definition);
    const existing = rows.get(title);
    if (existing) {
      if (code) {
        existing.codes.push(code);
      }
      if (existing.insurance !== insurance) {
        existing.insurance = undefined;
        existing.discount = undefined;
        existing.insuranceReason = 'Conflicting seeded insurance amounts';
      }
      if (existing.selfPay !== selfPay) {
        existing.selfPay = undefined;
        existing.discount = undefined;
        existing.selfPayReason = 'Conflicting seeded self-pay amounts';
      }
      if (existing.insurance !== undefined && existing.selfPay !== undefined) {
        existing.discount = existing.insurance - existing.selfPay;
      }
      continue;
    }
    rows.set(title, {
      definition,
      title,
      codes: code ? [code] : [],
      insurance,
      discount: insurance !== undefined && selfPay !== undefined ? insurance - selfPay : undefined,
      selfPay,
      insuranceReason: insurance === undefined ? 'Not billable to insurance' : undefined,
      selfPayReason:
        selfPay === undefined ? (SELF_PAY_REASONS[code ?? ''] ?? 'Insurance-only — not billed to self-pay') : undefined,
    });
  }
  return [...rows.values()];
}

function serviceGroup(row: ChargeRow): string {
  const code = row.codes[0] ?? row.definition.url?.split('/').pop();
  if (code === 'self-pay-bundle') {
    return 'Diagnostics';
  }
  if (code === 'no-show-fee') {
    return 'Programs';
  }
  if (code?.startsWith('994')) {
    return 'Remote patient monitoring';
  }
  if (code === '95800' || code === 'G0400') {
    return 'Diagnostics';
  }
  return 'Visits';
}

function money(value: Price): string {
  return value === undefined
    ? ''
    : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(value);
}

function codeNote(row: ChargeRow): string | undefined {
  if (row.codes.length > 1 && row.codes.every((code) => code.startsWith('992'))) {
    return 'E/M level 2–5, one amount';
  }
  if (row.definition.url?.endsWith('/self-pay-bundle')) {
    return 'not a CPT — intake + HSAT + results review';
  }
  return undefined;
}

function Amount({ value, reason, className }: { value: Price; reason?: string; className?: string }): JSX.Element {
  if (value === undefined) {
    return <span className={classes.na}>{reason}</span>;
  }
  return <span className={className ?? classes.amount}>{money(value)}</span>;
}

export function ChargemasterTab(): JSX.Element {
  const medplum = useMedplum();
  const [definitions, setDefinitions] = useState<ChargeItemDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    setLoading(true);
    medplum
      .searchResources('ChargeItemDefinition', { status: 'active' })
      .then((resources) => {
        if (active) {
          setDefinitions(resources);
          setError(undefined);
        }
      })
      .catch((reason: unknown) => {
        if (active) {
          Sentry.captureException(reason);
          setError(reason instanceof Error ? reason.message : 'Unable to load chargemaster.');
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [medplum]);

  const groups = useMemo(() => {
    const rows = getRows(definitions);
    const byGroup = new Map<string, ChargeRow[]>();
    for (const row of rows) {
      const group = serviceGroup(row);
      const groupRows = byGroup.get(group) ?? [];
      groupRows.push(row);
      byGroup.set(group, groupRows);
    }
    return [...byGroup.entries()];
  }, [definitions]);

  let tableContent: JSX.Element;
  if (loading) {
    tableContent = <Loading />;
  } else if (error) {
    tableContent = <p className={classes.empty}>{error}</p>;
  } else {
    tableContent = (
      <div className={classes.scroll}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th>Service</th>
              <th>Codes</th>
              <th className={classes.numeric}>Insurance</th>
              <th className={classes.numeric}>Self-pay discount</th>
              <th className={classes.numeric}>Self-pay price</th>
            </tr>
          </thead>
          <tbody>
            {groups.map(([group, rows]) => (
              <Fragment key={group}>
                <tr className={classes.group}>
                  <td colSpan={5}>{group}</td>
                </tr>
                {rows.map((row) => {
                  return (
                    <tr key={row.definition.id ?? row.definition.url}>
                      <td className={classes.service}>{row.title}</td>
                      <td>
                        {row.codes.length ? (
                          <>
                            <div className={classes.codes}>
                              {row.codes.map((code) => (
                                <span key={code} className={classes.code}>
                                  {code}
                                </span>
                              ))}
                            </div>
                            {codeNote(row) && <div className={classes.note}>{codeNote(row)}</div>}
                          </>
                        ) : (
                          <>
                            <span className={classes.dash}>—</span>
                            {codeNote(row) && <div className={classes.note}>{codeNote(row)}</div>}
                          </>
                        )}
                      </td>
                      <td className={classes.numeric}>
                        <Amount value={row.insurance} reason={row.insuranceReason} />
                      </td>
                      <td className={classes.numeric}>
                        {row.discount === undefined ? (
                          <span className={classes.dash}>—</span>
                        ) : (
                          <span className={classes.discount}>−{money(row.discount)}</span>
                        )}
                      </td>
                      <td className={classes.numeric}>
                        <Amount value={row.selfPay} reason={row.selfPayReason} />
                        {row.insurance !== undefined && row.selfPay !== undefined && (
                          <div className={classes.note}>
                            {money(row.insurance)} − {money(row.insurance - row.selfPay)}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className={classes.wrap}>
      <section className={classes.banner}>
        <div>
          <h2>Chargemaster</h2>
          <p>
            What every service costs to insurance and to a self-pay patient, and how the two relate. Self-pay is the
            chargemaster amount <strong>minus a published discount</strong> — not a separate fee schedule.
          </p>
        </div>
        <div className={classes.effective}>
          <span>Effective as of</span>
          <strong>August 14, 2026</strong>
        </div>
      </section>

      <div className={classes.lock}>
        <IconLock aria-hidden="true" />
        <div>
          <strong>Read-only.</strong> Amounts sourced from seed bundle app charges from. Changes go through a PR so
          seeded charges and this list stay aligned.
        </div>
      </div>

      <section className={classes.card} aria-label="Chargemaster prices">
        {tableContent}
        {!loading && !error && definitions.length === 0 && (
          <p className={classes.empty}>No active charge definitions found.</p>
        )}
        {!loading && !error && definitions.length > 0 && (
          <div className={classes.legend}>
            <div>
              <span className={`${classes.key} ${classes.keyDerived}`} />
              Self-pay discount is derived: insurance minus self-pay.
            </div>
            <div>
              <span className={`${classes.key} ${classes.keyNa}`} />
              An em dash means no self-pay price is configured.
            </div>
            <div>
              <span className={`${classes.key} ${classes.keyUnit}`} />
              Prices are shown per service unless a unit is stated.
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
